// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/utils/operationsApi.js
//
// Thin wrapper around /operations/list.
//
// The endpoint returns the registered operation catalogue plus each
// operation's input_schema, so the admin's post-trigger editor can
// render a form for every operation without hardcoding field lists.
//
// The response is cached in module scope for the lifetime of the page.
// Operation registrations only change when the Lambda is redeployed,
// so re-fetching on every modal open would be pure waste.

const backend_api_base_url = `${process.env.REACT_APP_SL_BE_BASE_ENV_PART}/${process.env.REACT_APP_STAGE_ENV}`;

function buildApiBase(region) {
  const regionLower = (region || "us").toLowerCase();
  return `https://${regionLower}.connector-handler2.${backend_api_base_url}`;
}

function getToken() {
  return localStorage.getItem("token") || "";
}

// Module-scope cache: { "<region>": { operations, fetchedAt } }
const _cache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch the registered operations for a region.
 *
 * Returns { operations: [...] }.
 *
 * Each operation is:
 *   {
 *     name, provider, required_scopes, display_name, description,
 *     input_schema: {
 *       <field>: { type, required, label, default?, help? }, ...
 *     }
 *   }
 *
 * Throws on network / non-2xx. Callers surface via their own UI.
 */
export async function listOperations({ region, force = false } = {}) {
  const regionLower = (region || "us").toLowerCase();
  const cached = _cache.get(regionLower);
  const now = Date.now();

  if (!force && cached && now - cached.fetchedAt < CACHE_TTL_MS) {
    return { operations: cached.operations };
  }

  const url = `${buildApiBase(region)}/operations/list`;

  const resp = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: getToken(),
    },
  });

  let body = null;
  try {
    body = await resp.json();
  } catch (_) {
    // non-JSON body — handled below
  }

  if (!resp.ok || !body?.success) {
    const msg =
      body?.error ||
      body?.detail ||
      body?.message ||
      `Failed to load operations (HTTP ${resp.status})`;
    throw new Error(msg);
  }

  const ops = Array.isArray(body.operations) ? body.operations : [];
  _cache.set(regionLower, { operations: ops, fetchedAt: now });

  return { operations: ops };
}

/**
 * Optional: bust the cache. Not needed today (the cache is per-page-
 * load and operations don't change without a redeploy), but kept so a
 * future "refresh operations" button has a hook.
 */
export function clearOperationsCache() {
  _cache.clear();
}